// Условия технического журнала в адресе страницы (contracts/ui.md, «/ops/log»; FR-032): номер
// операции, код ошибки, модуль, уровень не ниже, период и сортировка по времени. Номер операции
// приходит и ссылкой из «Фоновых задач»: /ops/log?operation=7F3K-92QD-1A2B — с дефисами,
// строчными, O вместо нуля номер находится. Период — даты в поясе установки; не выбран — сервер
// ищет за последние сутки, а номер операции — за весь срок хранения журнала.
import { OPERATION_PATTERN, logLevels, logModules, normalizeOperation } from '@kp/contracts';
import type { LogLevel, LogModule, LogQuery, SortValue } from '@kp/contracts';
import { dayEnd, dayStart } from '../../app/format.ts';

export type LogSort = SortValue<'at'>;

/** По умолчанию — новые сверху. */
export const DEFAULT_LOG_SORT: LogSort = '-at';

export interface LogSearch {
  /** Номер операции: 12 знаков без дефисов. */
  operation?: string;
  /** Код ошибки из каталога: SYS-001. */
  code?: string;
  module?: LogModule;
  /** Уровень не ниже: warn — предупреждения, ошибки и отказы. */
  level?: LogLevel;
  /** Первый день периода: 2026-10-14. */
  from?: string;
  /** Последний день периода включительно. */
  to?: string;
  /** at — старые сверху; без значения — новые сверху. */
  sort?: LogSort;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
export const ERROR_CODE = /^[A-Z]{2,5}-\d{3}$/;

/** Номер операции из поля поиска или адреса: «7f3k-92qd-1a2b» → «7F3K92QD1A2B»; не номер — null. */
export function operationOf(text: string): string | null {
  const value = normalizeOperation(text.trim());
  return OPERATION_PATTERN.test(value) ? value : null;
}

/** Код ошибки из поля: « sys-001 » → «SYS-001»; не код — null. */
export function errorCodeOf(text: string): string | null {
  const value = text.trim().toUpperCase();
  return ERROR_CODE.test(value) ? value : null;
}

function isOneOf<T extends string>(list: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (list as readonly string[]).includes(value);
}

function date(value: unknown): string | undefined {
  return typeof value === 'string' && DATE.test(value) ? value : undefined;
}

export function parseLogSearch(search: Record<string, unknown>): LogSearch {
  const result: LogSearch = {};
  const operation = typeof search.operation === 'string' ? operationOf(search.operation) : null;
  if (operation) result.operation = operation;
  const code = typeof search.code === 'string' ? errorCodeOf(search.code) : null;
  if (code) result.code = code;
  if (isOneOf(logModules, search.module)) result.module = search.module;
  if (isOneOf(logLevels, search.level)) result.level = search.level;
  const from = date(search.from);
  if (from) result.from = from;
  const to = date(search.to);
  if (to) result.to = to;
  if (search.sort === 'at' || search.sort === '-at') result.sort = search.sort;
  return result;
}

/**
 * Условия сужают список: «Ничего не найдено» и «Сбросить фильтры» вместо «Записей за этот период
 * нет» (ux.md, раздел 6). Период, выбранный человеком, — тоже условие; «Записей за этот период
 * нет» — только за период по умолчанию (последние сутки). Сортировка не сужает.
 */
export function hasLogFilters(search: LogSearch): boolean {
  return Boolean(
    search.operation ?? search.code ?? search.module ?? search.level ?? search.from ?? search.to,
  );
}

/** Условия запроса к серверу. */
export function logFilters(search: LogSearch, timeZone: string): LogQuery {
  return {
    operation: search.operation,
    code: search.code,
    module: search.module,
    level: search.level,
    from: search.from ? dayStart(search.from, timeZone) : undefined,
    to: search.to ? dayEnd(search.to, timeZone) : undefined,
    sort: search.sort ?? DEFAULT_LOG_SORT,
  };
}
